import { FC, Fragment, PropsWithChildren } from 'react';
import { Ace as AceNamespace } from 'ace-builds';

import { CreativeLibraryPanel, PanelIntent, PanelView } from 'modules/creativeLibrary';
import { useModalContext } from 'utils/contexts/modal';

import { Modals } from '../../types';
import { useSideBySideEditorContext } from '../SideBySideEditorContext';
import { getCurrentElementRange, getCurrentElement, isValidImgElement } from '../../utils';

export const CreativeLibraryContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { editor, selectedImage } = useSideBySideEditorContext();
  const { modalState, hideModal } = useModalContext<Modals>();

  const handleAssetInsert = (attrs: Record<string, string> = {}) => {
    const thisEditor = editor.current;
    if (thisEditor) {
      const target = thisEditor.selection.getRange();

      // Check if cursor is within an existing img tag using the existing utility
      // This uses character-by-character scanning instead of regex for better reliability
      const existingImgRange = getCurrentElementRange(thisEditor);

      // Validate that we actually have a valid image element before replacing
      let replaceRange = target;
      if (existingImgRange) {
        const element = getCurrentElement(thisEditor, existingImgRange);
        if (isValidImgElement(element)) {
          replaceRange = existingImgRange;
        }
        // If not a valid image element, fall back to current selection
      }

      const imgAttrStr = Object.keys(attrs)
        .reduce((attrPairs: string[], attrKey: string) => {
          // @ts-ignore
          const attrValue = attrs[attrKey].trim().replaceAll('"', '&quot;');
          if (attrValue) {
            attrPairs.push(`${attrKey}="${attrValue}"`);
          }
          return attrPairs;
        }, [])
        .join(' ');

      const eltHtml = `<img ${imgAttrStr} />`;
      const newElementRange = {
        start: replaceRange.start,
        end: { row: replaceRange.start.row, column: replaceRange.start.column + eltHtml.length },
      } as AceNamespace.Range;

      thisEditor.session.replace(replaceRange, eltHtml);
      thisEditor.selection.setRange(newElementRange);
      thisEditor.focus();
    }
  };

  return (
    <Fragment>
      {modalState === Modals.CreativeLibrary && (
        <CreativeLibraryPanel
          initView={selectedImage ? PanelView.Detail : PanelView.List}
          intent={selectedImage ? PanelIntent.Edit : PanelIntent.Insert}
          onClose={hideModal}
          onInsert={handleAssetInsert}
          selectedEditorImageNode={selectedImage}
        />
      )}
      {children}
    </Fragment>
  );
};
